import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Heart, Minus, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import Header24 from './components/Header24';
import Footer24 from './components/Footer24';

const ProductDetail24 = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [selectedSize, setSelectedSize] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [activeImage, setActiveImage] = useState(0);
    const [isWishlisted, setIsWishlisted] = useState(false);
    const [activeTab, setActiveTab] = useState('story');

    const product = {
        id: 1,
        name: 'Silk Chanderi Drape',
        price: 14500,
        description: 'A meditation on light and movement, this Chanderi silk drape captures the essence of a tradition that dates back to the Mughal courts. Each piece is woven on a handloom by master artisans in Chanderi, Madhya Pradesh.',
        story: 'The Chanderi tradition is one of India\'s most refined weaving legacies. Our drape reimagines this heritage through a contemporary lens, combining gossamer-thin silk with subtle geometric motifs that catch the light as you move. The fabric is so fine it can pass through a ring, yet holds a structure that drapes with architectural grace.',
        fabric: '100% Pure Silk Chanderi',
        craft: 'Handwoven on traditional pit looms in Chanderi, MP. Each piece takes approximately 15 days to weave.',
        care: 'Dry clean only. Store flat in muslin. Avoid direct sunlight for extended periods.',
        sizes: ['XS', 'S', 'M', 'L', 'XL'],
        images: [
            'https://images.pexels.com/photos/8980747/pexels-photo-8980747.jpeg?auto=compress&cs=tinysrgb&w=1200',
            'https://images.pexels.com/photos/7764611/pexels-photo-7764611.jpeg?auto=compress&cs=tinysrgb&w=1200',
            'https://images.pexels.com/photos/8285483/pexels-photo-8285483.jpeg?auto=compress&cs=tinysrgb&w=1200',
            'https://images.pexels.com/photos/6765164/pexels-photo-6765164.jpeg?auto=compress&cs=tinysrgb&w=1200',
        ],
    };

    const relatedProducts = [
        { id: 3, name: 'Heritage Banarasi Sari', price: 22000, image: 'https://images.pexels.com/photos/8285483/pexels-photo-8285483.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 5, name: 'Handwoven Stole', price: 5200, image: 'https://images.pexels.com/photos/6567607/pexels-photo-6567607.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 6, name: 'Linen Summer Set', price: 8800, image: 'https://images.pexels.com/photos/6765003/pexels-photo-6765003.jpeg?auto=compress&cs=tinysrgb&w=800' },
    ];

    const nextImage = () => setActiveImage(prev => (prev + 1) % product.images.length);
    const prevImage = () => setActiveImage(prev => (prev - 1 + product.images.length) % product.images.length);

    const tabs = [
        { key: 'story', label: 'The Story' },
        { key: 'details', label: 'Details & Craft' },
        { key: 'care', label: 'Care' },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,500&family=Inter:wght@300;400;500;600;700&display=swap');

                .dhara-pd {
                    min-height: 100vh;
                    background: #FFFFFF;
                }
                .dhara-pd-breadcrumb {
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 24px 40px;
                    font-family: 'Inter', sans-serif;
                    font-size: 12px;
                    color: #6B6B6B;
                }
                .dhara-pd-breadcrumb span {
                    cursor: pointer;
                    transition: color 0.3s ease;
                }
                .dhara-pd-breadcrumb span:hover {
                    color: #00553A;
                }
                .dhara-pd-main {
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 0 40px 100px;
                    display: grid;
                    grid-template-columns: 1.2fr 1fr;
                    gap: 80px;
                    align-items: start;
                }
                .dhara-pd-gallery {
                    position: relative;
                }
                .dhara-pd-image-main {
                    width: 100%;
                    aspect-ratio: 3/4;
                    object-fit: cover;
                    background: #FAFAF8;
                    margin-bottom: 16px;
                }
                .dhara-pd-gallery-nav {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    background: rgba(255,255,255,0.9);
                    border: none;
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    color: #1A1A1A;
                }
                .dhara-pd-gallery-nav:hover {
                    background: #FFFFFF;
                    box-shadow: 0 2px 12px rgba(0,0,0,0.1);
                }
                .dhara-pd-gallery-nav.prev { left: 16px; }
                .dhara-pd-gallery-nav.next { right: 16px; }
                .dhara-pd-thumbs {
                    display: flex;
                    gap: 12px;
                }
                .dhara-pd-thumb {
                    width: 80px;
                    height: 100px;
                    object-fit: cover;
                    cursor: pointer;
                    opacity: 0.5;
                    transition: opacity 0.3s ease;
                    border: none;
                    padding: 0;
                    background: none;
                }
                .dhara-pd-thumb.active,
                .dhara-pd-thumb:hover {
                    opacity: 1;
                }
                .dhara-pd-info {
                    padding-top: 20px;
                }
                .dhara-pd-name {
                    font-family: 'Playfair Display', serif;
                    font-size: 36px;
                    font-weight: 400;
                    color: #1A1A1A;
                    margin-bottom: 16px;
                    letter-spacing: -0.5px;
                    line-height: 1.2;
                }
                .dhara-pd-price {
                    font-family: 'Inter', sans-serif;
                    font-size: 18px;
                    font-weight: 300;
                    color: #6B6B6B;
                    margin-bottom: 32px;
                }
                .dhara-pd-desc {
                    font-family: 'Inter', sans-serif;
                    font-size: 15px;
                    color: #6B6B6B;
                    line-height: 1.8;
                    margin-bottom: 40px;
                    max-width: 480px;
                }
                .dhara-pd-divider {
                    height: 1px;
                    background: #E5E5E5;
                    margin: 32px 0;
                }
                .dhara-pd-label {
                    font-family: 'Inter', sans-serif;
                    font-size: 11px;
                    font-weight: 600;
                    letter-spacing: 2px;
                    text-transform: uppercase;
                    color: #1A1A1A;
                    margin-bottom: 16px;
                }
                .dhara-pd-sizes {
                    display: flex;
                    gap: 10px;
                    margin-bottom: 32px;
                }
                .dhara-pd-size-btn {
                    width: 48px;
                    height: 48px;
                    border: 1px solid #E5E5E5;
                    background: transparent;
                    font-family: 'Inter', sans-serif;
                    font-size: 13px;
                    color: #1A1A1A;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .dhara-pd-size-btn:hover {
                    border-color: #1A1A1A;
                }
                .dhara-pd-size-btn.active {
                    background: #1A1A1A;
                    color: #FFFFFF;
                    border-color: #1A1A1A;
                }
                .dhara-pd-quantity {
                    display: flex;
                    align-items: center;
                    gap: 0;
                    margin-bottom: 40px;
                }
                .dhara-pd-qty-btn {
                    width: 48px;
                    height: 48px;
                    border: 1px solid #E5E5E5;
                    background: transparent;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #1A1A1A;
                    transition: border-color 0.3s ease;
                }
                .dhara-pd-qty-btn:hover {
                    border-color: #1A1A1A;
                }
                .dhara-pd-qty-val {
                    width: 60px;
                    height: 48px;
                    border-top: 1px solid #E5E5E5;
                    border-bottom: 1px solid #E5E5E5;
                    border-left: none;
                    border-right: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    color: #1A1A1A;
                }
                .dhara-pd-actions {
                    display: flex;
                    gap: 12px;
                    margin-bottom: 24px;
                }
                .dhara-pd-add-btn {
                    flex: 1;
                    height: 52px;
                    background: #00553A;
                    color: #FFFFFF;
                    border: none;
                    font-family: 'Inter', sans-serif;
                    font-size: 13px;
                    font-weight: 500;
                    letter-spacing: 1.5px;
                    text-transform: uppercase;
                    cursor: pointer;
                    transition: background 0.3s ease;
                }
                .dhara-pd-add-btn:hover {
                    background: #006B4A;
                }
                .dhara-pd-wish-btn {
                    width: 52px;
                    height: 52px;
                    border: 1px solid #E5E5E5;
                    background: transparent;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: border-color 0.3s ease;
                }
                .dhara-pd-wish-btn:hover {
                    border-color: #00553A;
                }
                .dhara-pd-tabs {
                    display: flex;
                    gap: 32px;
                    border-bottom: 1px solid #E5E5E5;
                    margin-top: 48px;
                }
                .dhara-pd-tab {
                    font-family: 'Inter', sans-serif;
                    font-size: 13px;
                    font-weight: 400;
                    letter-spacing: 0.5px;
                    color: #6B6B6B;
                    padding: 12px 0;
                    border: none;
                    background: none;
                    cursor: pointer;
                    position: relative;
                    transition: color 0.3s ease;
                }
                .dhara-pd-tab.active {
                    color: #1A1A1A;
                }
                .dhara-pd-tab.active::after {
                    content: '';
                    position: absolute;
                    bottom: -1px;
                    left: 0;
                    right: 0;
                    height: 1px;
                    background: #1A1A1A;
                }
                .dhara-pd-tab-content {
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    color: #6B6B6B;
                    line-height: 1.9;
                    padding: 28px 0;
                    max-width: 480px;
                }

                /* Related Section */
                .dhara-pd-related {
                    max-width: 1400px;
                    margin: 0 auto;
                    padding: 0 40px 120px;
                }
                .dhara-pd-related-title {
                    font-family: 'Playfair Display', serif;
                    font-size: 28px;
                    font-weight: 400;
                    color: #1A1A1A;
                    margin-bottom: 48px;
                    letter-spacing: -0.5px;
                }
                .dhara-pd-related-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 24px;
                }
                .dhara-pd-related-card {
                    cursor: pointer;
                }
                .dhara-pd-related-img {
                    width: 100%;
                    aspect-ratio: 3/4;
                    object-fit: cover;
                    background: #FAFAF8;
                    margin-bottom: 16px;
                    transition: opacity 0.3s ease;
                }
                .dhara-pd-related-card:hover .dhara-pd-related-img {
                    opacity: 0.9;
                }
                .dhara-pd-related-name {
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    color: #1A1A1A;
                    margin-bottom: 4px;
                }
                .dhara-pd-related-price {
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    color: #6B6B6B;
                    font-weight: 300;
                }

                @media (max-width: 768px) {
                    .dhara-pd-main {
                        grid-template-columns: 1fr;
                        gap: 40px;
                        padding: 0 20px 60px;
                    }
                    .dhara-pd-breadcrumb { padding: 16px 20px; }
                    .dhara-pd-name { font-size: 28px; }
                    .dhara-pd-related { padding: 0 20px 80px; }
                    .dhara-pd-related-grid { grid-template-columns: repeat(2, 1fr); }
                }
            `}</style>

            <Header24 />

            <main className="dhara-pd">
                {/* Breadcrumb */}
                <div className="dhara-pd-breadcrumb">
                    <span onClick={() => navigate('/template24')}>Home</span>
                    {' / '}
                    <span onClick={() => navigate('/template24/shop')}>Shop</span>
                    {' / '}
                    <span style={{ color: '#1A1A1A' }}>{product.name}</span>
                </div>

                {/* Main Content */}
                <div className="dhara-pd-main">
                    {/* Gallery */}
                    <div className="dhara-pd-gallery">
                        <img
                            className="dhara-pd-image-main"
                            src={product.images[activeImage]}
                            alt={product.name}
                        />
                        <button className="dhara-pd-gallery-nav prev" onClick={prevImage}>
                            <ChevronLeft size={20} strokeWidth={1.5} />
                        </button>
                        <button className="dhara-pd-gallery-nav next" onClick={nextImage}>
                            <ChevronRight size={20} strokeWidth={1.5} />
                        </button>
                        <div className="dhara-pd-thumbs">
                            {product.images.map((img, i) => (
                                <button
                                    key={i}
                                    className={`dhara-pd-thumb ${activeImage === i ? 'active' : ''}`}
                                    onClick={() => setActiveImage(i)}
                                >
                                    <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Info */}
                    <div className="dhara-pd-info">
                        <h1 className="dhara-pd-name">{product.name}</h1>
                        <p className="dhara-pd-price">{'\u20B9'}{product.price.toLocaleString('en-IN')}</p>
                        <p className="dhara-pd-desc">{product.description}</p>

                        <div className="dhara-pd-divider" />

                        {/* Size */}
                        <div className="dhara-pd-label">Size</div>
                        <div className="dhara-pd-sizes">
                            {product.sizes.map(size => (
                                <button
                                    key={size}
                                    className={`dhara-pd-size-btn ${selectedSize === size ? 'active' : ''}`}
                                    onClick={() => setSelectedSize(size)}
                                >
                                    {size}
                                </button>
                            ))}
                        </div>

                        {/* Quantity */}
                        <div className="dhara-pd-label">Quantity</div>
                        <div className="dhara-pd-quantity">
                            <button
                                className="dhara-pd-qty-btn"
                                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                            >
                                <Minus size={16} strokeWidth={1.5} />
                            </button>
                            <div className="dhara-pd-qty-val">{quantity}</div>
                            <button
                                className="dhara-pd-qty-btn"
                                onClick={() => setQuantity(quantity + 1)}
                            >
                                <Plus size={16} strokeWidth={1.5} />
                            </button>
                        </div>

                        {/* Actions */}
                        <div className="dhara-pd-actions">
                            <button className="dhara-pd-add-btn">Add to Bag</button>
                            <button
                                className="dhara-pd-wish-btn"
                                onClick={() => setIsWishlisted(!isWishlisted)}
                            >
                                <Heart
                                    size={20}
                                    strokeWidth={1.5}
                                    fill={isWishlisted ? '#00553A' : 'none'}
                                    color={isWishlisted ? '#00553A' : '#1A1A1A'}
                                />
                            </button>
                        </div>

                        {/* Tabs */}
                        <div className="dhara-pd-tabs">
                            {tabs.map(tab => (
                                <button
                                    key={tab.key}
                                    className={`dhara-pd-tab ${activeTab === tab.key ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.key)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                        <div className="dhara-pd-tab-content">
                            {activeTab === 'story' && <p>{product.story}</p>}
                            {activeTab === 'details' && (
                                <div>
                                    <p style={{ marginBottom: '16px' }}><strong style={{ color: '#1A1A1A', fontWeight: 500 }}>Fabric:</strong> {product.fabric}</p>
                                    <p>{product.craft}</p>
                                </div>
                            )}
                            {activeTab === 'care' && <p>{product.care}</p>}
                        </div>
                    </div>
                </div>

                {/* From This Collection */}
                <div className="dhara-pd-related">
                    <h2 className="dhara-pd-related-title">From This Collection</h2>
                    <div className="dhara-pd-related-grid">
                        {relatedProducts.map(item => (
                            <div
                                key={item.id}
                                className="dhara-pd-related-card"
                                onClick={() => navigate(`/template24/product/${item.id}`)}
                            >
                                <img className="dhara-pd-related-img" src={item.image} alt={item.name} />
                                <div className="dhara-pd-related-name">{item.name}</div>
                                <div className="dhara-pd-related-price">{'\u20B9'}{item.price.toLocaleString('en-IN')}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </main>

            <Footer24 />
        </>
    );
};

export default ProductDetail24;
